import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = ({ currentLanguage }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const translations = {
    en: {
      title: "Frequently Asked Questions",
      faqs: [
        {
          question: "How accurate is the money duration calculator?",
          answer: "The calculator provides estimates based on your current savings and monthly expenses. Actual results may vary depending on unexpected expenses, income changes, or variations in spending patterns. It's designed to give you a general planning guideline."
        },
        {
          question: "What should I do if my money will run out in less than 3 months?",
          answer: "If you're in the CRITICAL zone, consider immediate actions such as reducing non-essential expenses, finding additional income sources, negotiating payment plans for bills, or seeking financial assistance programs. Creating a detailed budget and tracking every expense can help identify areas to cut costs."
        },
        {
          question: "Should I include emergency fund in my savings amount?",
          answer: "Yes, include all liquid savings that you can access for living expenses. However, it's recommended to maintain a separate emergency fund of 3-6 months of expenses for unexpected situations beyond regular monthly costs."
        },
        {
          question: "How often should I recalculate my money duration?",
          answer: "Recalculate monthly or whenever there's a significant change in your financial situation, such as income changes, major expenses, or lifestyle adjustments. Regular monitoring helps you stay on track with your financial goals."
        },
        {
          question: "What expenses should I include in monthly expenses?",
          answer: "Include all recurring costs such as rent/mortgage, utilities, groceries, transportation, insurance, debt payments, subscriptions, and average discretionary spending. Be realistic and comprehensive to get accurate results."
        }
      ]
    },
    es: {
      title: "Preguntas Frecuentes",
      faqs: [
        {
          question: "¿Qué tan precisa es la calculadora de duración del dinero?",
          answer: "La calculadora proporciona estimaciones basadas en sus ahorros actuales y gastos mensuales. Los resultados reales pueden variar según gastos inesperados, cambios de ingresos o variaciones en los patrones de gasto. Está diseñada para darle una guía general de planificación."
        },
        {
          question: "¿Qué debo hacer si mi dinero se agotará en menos de 3 meses?",
          answer: "Si está en la zona CRÍTICA, considere acciones inmediatas como reducir gastos no esenciales, encontrar fuentes de ingresos adicionales, negociar planes de pago para facturas o buscar programas de asistencia financiera. Crear un presupuesto detallado y rastrear cada gasto puede ayudar a identificar áreas para reducir costos."
        },
        {
          question: "¿Debo incluir el fondo de emergencia en mi monto de ahorros?",
          answer: "Sí, incluya todos los ahorros líquidos a los que pueda acceder para gastos de vida. Sin embargo, se recomienda mantener un fondo de emergencia separado de 3-6 meses de gastos para situaciones inesperadas más allá de los costos mensuales regulares."
        },
        {
          question: "¿Con qué frecuencia debo recalcular la duración de mi dinero?",
          answer: "Recalcule mensualmente o cuando haya un cambio significativo en su situación financiera, como cambios de ingresos, gastos importantes o ajustes de estilo de vida. El monitoreo regular le ayuda a mantenerse en el camino con sus objetivos financieros."
        },
        {
          question: "¿Qué gastos debo incluir en los gastos mensuales?",
          answer: "Incluya todos los costos recurrentes como alquiler/hipoteca, servicios públicos, comestibles, transporte, seguros, pagos de deudas, suscripciones y gasto discrecional promedio. Sea realista y completo para obtener resultados precisos."
        }
      ]
    },
    pt: {
      title: "Perguntas Frequentes",
      faqs: [
        {
          question: "Quão precisa é a calculadora de duração do dinheiro?",
          answer: "A calculadora fornece estimativas com base em suas economias atuais e despesas mensais. Os resultados reais podem variar dependendo de despesas inesperadas, mudanças de renda ou variações nos padrões de gastos. É projetada para fornecer uma diretriz geral de planejamento."
        },
        {
          question: "O que devo fazer se meu dinheiro acabar em menos de 3 meses?",
          answer: "Se você está na zona CRÍTICA, considere ações imediatas como reduzir despesas não essenciais, encontrar fontes de renda adicionais, negociar planos de pagamento para contas ou procurar programas de assistência financeira. Criar um orçamento detalhado e rastrear cada despesa pode ajudar a identificar áreas para cortar custos."
        },
        {
          question: "Devo incluir o fundo de emergência no valor da poupança?",
          answer: "Sim, inclua todas as economias líquidas que você pode acessar para despesas de vida. No entanto, é recomendado manter um fundo de emergência separado de 3-6 meses de despesas para situações inesperadas além dos custos mensais regulares."
        },
        {
          question: "Com que frequência devo recalcular a duração do meu dinheiro?",
          answer: "Recalcule mensalmente ou sempre que houver uma mudança significativa em sua situação financeira, como mudanças de renda, despesas importantes ou ajustes de estilo de vida. O monitoramento regular ajuda você a permanecer no caminho com seus objetivos financeiros."
        },
        {
          question: "Quais despesas devo incluir nas despesas mensais?",
          answer: "Inclua todos os custos recorrentes como aluguel/hipoteca, serviços públicos, mantimentos, transporte, seguros, pagamentos de dívidas, assinaturas e gastos discricionários médios. Seja realista e abrangente para obter resultados precisos."
        }
      ]
    },
    fr: {
      title: "Questions Fréquemment Posées",
      faqs: [
        {
          question: "Quelle est la précision du calculateur de durée d\'argent?",
          answer: "Le calculateur fournit des estimations basées sur vos économies actuelles et vos dépenses mensuelles. Les résultats réels peuvent varier en fonction des dépenses imprévues, des changements de revenus ou des variations dans les habitudes de dépenses. Il est conçu pour vous donner une ligne directrice générale de planification."
        },
        {
          question: "Que dois-je faire si mon argent s'épuise dans moins de 3 mois?",
          answer: "Si vous êtes dans la zone CRITIQUE, envisagez des actions immédiates telles que réduire les dépenses non essentielles, trouver des sources de revenus supplémentaires, négocier des plans de paiement pour les factures ou rechercher des programmes d'assistance financière. Créer un budget détaillé et suivre chaque dépense peut aider à identifier les domaines où réduire les coûts."
        },
        {
          question: "Dois-je inclure le fonds d\'urgence dans mon montant d\'épargne?",
          answer: "Oui, incluez toutes les économies liquides auxquelles vous pouvez accéder pour les dépenses de vie. Cependant, il est recommandé de maintenir un fonds d'urgence séparé de 3 à 6 mois de dépenses pour les situations inattendues au-delà des coûts mensuels réguliers."
        },
        {
          question: "À quelle fréquence dois-je recalculer la durée de mon argent?",
          answer: "Recalculez mensuellement ou chaque fois qu'il y a un changement significatif dans votre situation financière, comme des changements de revenus, des dépenses importantes ou des ajustements de style de vie. La surveillance régulière vous aide à rester sur la bonne voie avec vos objectifs financiers."
        },
        {
          question: "Quelles dépenses dois-je inclure dans les dépenses mensuelles?",
          answer: "Incluez tous les coûts récurrents tels que loyer/hypothèque, services publics, épicerie, transport, assurance, paiements de dettes, abonnements et dépenses discrétionnaires moyennes. Soyez réaliste et complet pour obtenir des résultats précis."
        }
      ]
    },
    de: {
      title: "Häufig Gestellte Fragen",
      faqs: [
        {
          question: "Wie genau ist der Geldlaufzeit-Rechner?",
          answer: "Der Rechner liefert Schätzungen basierend auf Ihren aktuellen Ersparnissen und monatlichen Ausgaben. Die tatsächlichen Ergebnisse können je nach unerwarteten Ausgaben, Einkommensänderungen oder Variationen in den Ausgabemustern variieren. Er ist darauf ausgelegt, Ihnen eine allgemeine Planungsrichtlinie zu geben."
        },
        {
          question: "Was soll ich tun, wenn mein Geld in weniger als 3 Monaten ausgeht?",
          answer: "Wenn Sie sich in der KRITISCHEN Zone befinden, erwägen Sie sofortige Maßnahmen wie die Reduzierung nicht wesentlicher Ausgaben, das Finden zusätzlicher Einkommensquellen, das Aushandeln von Zahlungsplänen für Rechnungen oder die Suche nach finanziellen Hilfsprogrammen. Das Erstellen eines detaillierten Budgets und das Verfolgen jeder Ausgabe kann helfen, Bereiche zur Kostensenkung zu identifizieren."
        },
        {
          question: "Sollte ich den Notfallfonds in meinen Sparbetrag einbeziehen?",
          answer: "Ja, schließen Sie alle liquiden Ersparnisse ein, auf die Sie für Lebenshaltungskosten zugreifen können. Es wird jedoch empfohlen, einen separaten Notfallfonds von 3-6 Monaten Ausgaben für unerwartete Situationen über die regulären monatlichen Kosten hinaus zu führen."
        },
        {
          question: "Wie oft sollte ich meine Geldlaufzeit neu berechnen?",
          answer: "Berechnen Sie monatlich neu oder immer dann, wenn es eine signifikante Änderung in Ihrer finanziellen Situation gibt, wie Einkommensänderungen, größere Ausgaben oder Lebensstilanpassungen. Regelmäßige Überwachung hilft Ihnen, auf Kurs mit Ihren finanziellen Zielen zu bleiben."
        },
        {
          question: "Welche Ausgaben sollte ich in die monatlichen Ausgaben einbeziehen?",
          answer: "Schließen Sie alle wiederkehrenden Kosten wie Miete/Hypothek, Nebenkosten, Lebensmittel, Transport, Versicherung, Schuldenzahlungen, Abonnements und durchschnittliche diskretionäre Ausgaben ein. Seien Sie realistisch und umfassend, um genaue Ergebnisse zu erhalten."
        }
      ]
    }
  };

  const t = translations?.[currentLanguage] || translations?.en;

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="w-full max-w-2xl mx-auto mt-8 md:mt-10 lg:mt-12">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-6 md:mb-8">
        {t?.title}
      </h2>
      <div className="space-y-4">
        {t?.faqs?.map((faq, index) => (
          <div
            key={index}
            className="bg-card border border-border rounded-lg overflow-hidden shadow-elevation-1"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full flex items-center justify-between p-4 md:p-5 lg:p-6 text-left hover:bg-muted/50 transition-smooth"
              aria-expanded={openIndex === index}
            >
              <span className="text-base md:text-lg font-medium text-foreground pr-4">
                {faq?.question}
              </span>
              <Icon
                name={openIndex === index ? "ChevronUp" : "ChevronDown"}
                size={20}
                color="var(--color-foreground)"
                className="flex-shrink-0"
              />
            </button>

            {openIndex === index && (
              <div className="px-4 md:px-5 lg:px-6 pb-4 md:pb-5 lg:pb-6">
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  {faq?.answer}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQSection;